import {
    type AfterViewInit,
    Component,
    type ElementRef,
    inject,
    type OnInit,
    ViewChild,
} from "@angular/core";
import {
    FormBuilder,
    FormControl,
    type FormGroup,
    Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { resetStores } from "@datorama/akita";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { combineLatest } from "rxjs";
import { finalize } from "rxjs/operators";
import {
    AccountService,
    AuthServerProvider,
    LoginService,
    type MfaRequired,
    StateStorageService,
} from "../../core";
import { OAuthHandoffService } from "../../core/login/oauth-handoff.service";
import { OAuthLoginService } from "../../core/login/oauth-login.service";
import {
    EmitEvent,
    Event,
    EventService,
} from "../../shared/events/events.service";
import {
    FeatureFlagService,
    PublicFeatureFlags,
} from "../../shared/feature-flag/feature-flag.service";
import { SearchBarService } from "../../shared/search-bar/search-bar.service";

interface LoginForm {
    readonly username: FormControl<string>;
    readonly password: FormControl<string>;
}

@Component({
    templateUrl: "./login.component.html",
    styleUrl: "./login.component.scss",
    standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
})
export class LoginComponent implements OnInit, AfterViewInit {
    @ViewChild("usernameField", { static: true })
    public usernameField: ElementRef;

    private readonly stateStorageService = inject(StateStorageService);
    private readonly searchBarService = inject(SearchBarService);
    private readonly authJWTService = inject(AuthServerProvider);
    private readonly loginService = inject(LoginService);
    private readonly eventService = inject(EventService);
    private readonly modalService = inject(NgbModal);
    private readonly route = inject(ActivatedRoute);
    private readonly fb = inject(FormBuilder);
    private readonly router = inject(Router);
    private readonly oAuthLoginService = inject(OAuthLoginService);
    private readonly accountService = inject(AccountService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly oauthHandoffService = inject(OAuthHandoffService);

    authenticationError: boolean;
    errorMessage: string;
    saving: boolean;
    source: string;
    private oauthCallback: string | null = null;
    private oauthRequestId: string | null = null;
    private handoffInProgress: boolean = false;
    loginForm: FormGroup<LoginForm> = this.fb.group<LoginForm>({
        username: new FormControl("", [Validators.required, Validators.email]),
        password: new FormControl("", Validators.required),
    });
    submitted: boolean = false;
    oauthEnabled: boolean = false;
    oauthLoading: boolean = false;

    get f(): LoginForm {
        return this.loginForm.controls;
    }

    ngOnInit(): void {
        const oauthError = history.state?.oauth_error;
        if (oauthError) {
            this.authenticationError = true;
            this.errorMessage = oauthError;
        }

        this.source = this.route.snapshot.queryParamMap.get("source");
        this.oauthCallback =
            this.route.snapshot.queryParamMap.get("oauth_callback");
        this.oauthRequestId =
            this.route.snapshot.queryParamMap.get("request_id");

        // Drop any stale handoff state from an abandoned SP flow, so a later
        // normal login in the same tab can't trigger a redirect to the old SP.
        if (!this.oauthCallback) {
            this.oauthHandoffService.clearPendingHandoff();
        } else if (this.accountService.hasAuthToken()) {
            // Already authenticated and the SP is asking for a handoff — complete it
            // directly without logging the user out or making them re-authenticate.
            this.handoffInProgress = true;
            this.oauthHandoffService.stashHandoffParams(
                this.oauthCallback,
                this.oauthRequestId,
            );
            this.performOAuthHandoff();
            return;
        }

        combineLatest([
            this.accountService.getFirmInfo(),
            this.featureFlagService.isPublicFeatureEnabled(
                PublicFeatureFlags.OAUTH_LOGIN,
            ),
        ]).subscribe(
            ([firmInfo, featureEnabled]) =>
                (this.oauthEnabled =
                    firmInfo?.enableOAuthLogin && featureEnabled),
        );
    }

    ngAfterViewInit(): void {
        if (this.handoffInProgress) {
            // Mid-handoff with an existing session — don't tear down auth state.
            return;
        }
        resetStores();
        this.searchBarService.clearSearchBarFromLocalStorage();
        this.authJWTService.logout().subscribe();
        this.eventService.emit(new EmitEvent(Event.REFRESH_NAV, "true"));
        this.usernameField.nativeElement.focus();
        this.modalService.dismissAll();
    }

    login(): void {
        this.submitted = true;
        if (!this.loginForm.valid) {
            return;
        }

        this.saving = true;
        this.loginService
            .login(this.loginForm.value.username, this.loginForm.value.password)
            .subscribe({
                next: (data) =>
                    this.handleAuthSuccess(data, this.loginForm.value.username),
                error: (err) => {
                    console.error(err);
                    this.authenticationError = true;
                    this.saving = false;
                },
            });
    }

    loginWithMicrosoft(): void {
        this.oauthLoading = true;
        this.oAuthLoginService
            .getMicrosoftAuthUrl()
            .pipe(finalize(() => (this.oauthLoading = false)))
            .subscribe({
                next: (response) => {
                    if (this.oauthCallback) {
                        this.oauthHandoffService.stashHandoffParams(
                            this.oauthCallback,
                            this.oauthRequestId,
                        );
                    }
                    this.redirectTo(response.authUrl);
                },
                error: (err) => {
                    console.error("Failed to get OAuth URL", err);
                    this.authenticationError = true;
                    this.errorMessage =
                        "Unable to connect to Microsoft. Please try again.";
                },
            });
    }

    private redirectTo(url: string): void {
        window.location.href = url;
    }

    private handleAuthSuccess(
        data: MfaRequired | null,
        username: string,
    ): void {
        if (data?.mfaType && username) {
            if (this.oauthCallback) {
                this.oauthHandoffService.stashHandoffParams(
                    this.oauthCallback,
                    this.oauthRequestId,
                );
            }
            this.router.navigate(["/login", "verify"], {
                queryParams: { mfaType: data.mfaType, username },
            });
        } else if (this.oauthCallback) {
            this.oauthHandoffService.stashHandoffParams(
                this.oauthCallback,
                this.oauthRequestId,
            );
            this.performOAuthHandoff();
        } else {
            this.authenticationError = false;
            this.eventService.emit(new EmitEvent(Event.REFRESH_NAV, "true"));

            const redirect = this.stateStorageService.getUrl();
            if (redirect) {
                this.stateStorageService.storeUrl(null);
                this.router.navigateByUrl(redirect);
            } else {
                this.router.navigate(["/"]);
            }
        }
    }

    private performOAuthHandoff(): void {
        this.oauthHandoffService.completeHandoff().subscribe({
            error: (err) => {
                console.error("OAuth handoff failed", err);
                this.authenticationError = true;
                this.errorMessage = "OAuth handoff failed. Please try again.";
                this.saving = false;
            },
        });
    }
}
